import { TableColumn } from 'tea-component';
import { uniqBy } from 'lodash-es';
import { BOOL_OPTIONS } from '@gp/components/BoolSelect';
import { generateMockId } from '@gp/utils/common';
import { CommonColumnBuilders, ICommonColumnChangeHandler } from '@gp/utils/columns';
import { buildProjectSelectColumn } from '../../../../utils/columns';
import { getIsDisabled } from '../../../../utils';
import { BoolFlag } from '@gp/enums/common';
import { MilestoneDateStatus } from '@gp/enums/milestone';
import { DPS_REPORT_FORM_CONTROL_PLACEHOLDER } from '../../../../constants';

import type { IDPSReportChangeDTO, IDPSReportChangeVO } from '@gp/types/dpsReport/v2';

export const CARD_TITLE = 'Pending Memos/Decisions';

const BOOL_FLAGS = [BoolFlag.True, BoolFlag.False];
const APPROVALS = ['Pass', 'Fail', 'Redo', 'Pending Approval'];
const APPROVALS_OPTIONS = APPROVALS.map((approval) => ({
  value: approval,
  text: approval,
}));

/**
 * 组装Changes数据 重复的标准，后续会根据这个值 判定数据是否重复
 * @param data Changes对象
 * @returns Changes数据 重复的标准，后续会根据这个值 判定数据是否重复
 */
const getChangeRepeatCheckKey = (data?: IDPSReportChangeDTO): string => {
  const {
    project_id: projectId,
    milestone_id: milestoneId,
    field,
    new_value: newValue,
    original_value: originalValue,
  } = data || {};
  return !projectId || !milestoneId ? '' : `${projectId}_${milestoneId}_${field}_${newValue}_${originalValue}`;
};

export const buildRecord = (): IDPSReportChangeVO => ({
  mock_id: generateMockId(),
  project_id: '',
  project: '',
  platform: [],
  image_name: '',
  priority: '',
  cover: '',
  studio: '',
  studio_id: '',
  milestone_id: '',
  milestone: '',
  date_confirm: MilestoneDateStatus.Confirm,
  date: '',
  is_gr: BoolFlag.False,
  is_launch: BoolFlag.False,
  field: '',
  new_value: '',
  original_value: '',
  approved: '',
  memo: '',
  next_step: '',
});

/**
 * 针对即将来临的Changess集合，进行去重处理
 * 1、即将来临的Changess集合 内部去重，根据getChangeRepeatCheckKey方法生成的 重复判定标准去重
 * 2、结合当前的Changess集合 进行去重，过滤掉当前Changess集合中已有的，根据getChangeRepeatCheckKey方法生成的 重复判定标准去重
 * @param commingList 即将来临的Changess集合
 * @param currentList 当前的Changess集合
 * @returns 即将来临的Changess集合（经过去重处理的）
 */
export const removeDuplicatedChanges = (
  commingList?: IDPSReportChangeDTO[],
  currentList?: IDPSReportChangeDTO[],
): IDPSReportChangeDTO[] => {
  if (!Array.isArray(commingList) || !Array.isArray(currentList)) return [];
  if (!Array.isArray(commingList)) return currentList;
  if (!Array.isArray(currentList)) return commingList;
  // 当前Changess集合的 去重标准映射，value没用，只需要看key是否匹配即可
  const repeatCheckKeys = currentList.reduce<Record<string, true>>((acc, cur) => {
    const repeatCheckKey = getChangeRepeatCheckKey(cur);
    return { ...acc, [repeatCheckKey]: true };
  }, {});
  return uniqBy(commingList, getChangeRepeatCheckKey).filter((commingItem) => {
    const commingItemRepeatCheckKey = getChangeRepeatCheckKey(commingItem);
    return !repeatCheckKeys[commingItemRepeatCheckKey];
  });
};

export const getColumns = (props: {
  projects?: IGPSelectOptionWithGroup[];
  onProjectChange?: (payload: string, cvm: IDPSReportChangeVO) => void;
  onChange?: ICommonColumnChangeHandler<IDPSReportChangeVO>;
}) => {
  const { projects, onProjectChange, onChange } = props || {};
  const commonCfg = {
    placeholder: DPS_REPORT_FORM_CONTROL_PLACEHOLDER,
    getIsDisabled: (cvm: IDPSReportChangeVO) => getIsDisabled(cvm),
  };
  return [
    buildProjectSelectColumn({
      options: projects,
      onChange: onProjectChange,
    }),
    CommonColumnBuilders.buildTextareaColumn({
      ...commonCfg,
      key: 'field',
      header: 'Field of Change',
      onChange,
    }),
    CommonColumnBuilders.buildTextareaColumn({
      ...commonCfg,
      key: 'new_value',
      header: 'New Value',
      onChange,
    }),
    CommonColumnBuilders.buildTextareaColumn({
      ...commonCfg,
      key: 'original_value',
      header: 'Original Value',
      onChange,
    }),
    CommonColumnBuilders.buildCustomizeSelectColumn({
      ...commonCfg,
      key: 'approved',
      header: 'Approval',
      className: 'dbg-dps-report-approval-customize-select',
      boxClassName: 'dbg-dps-report-approval-customize-select-box',
      dropdownOverlayBoxClassName: 'dbg-dps-report-approval-customize-select-overlay-box',
      button: (cvm: IDPSReportChangeVO) => cvm?.approved,
      customizePlaceholder: 'Please input other',
      matchButtonWidth: true,
      searchable: false,
      clearable: true,
      onClear: (cvm, key, rowKey, rowIndex) => {
        onChange?.(cvm, key, '', rowKey, rowIndex);
      },
      options: APPROVALS_OPTIONS,
      onChange,
    }),
    CommonColumnBuilders.buildBoolSelectColumn({
      ...commonCfg,
      key: 'memo',
      header: 'Memo',
      button: (cvm: IDPSReportChangeVO) => {
        const { memo } = cvm || {};
        if (!BOOL_FLAGS.includes(memo as BoolFlag)) return memo as string;
        const boolOption = BOOL_OPTIONS.find((option) => option?.value === memo);
        return (boolOption?.text || memo || '') as string;
      },
      onChange,
    }),
    CommonColumnBuilders.buildTextareaColumn({
      ...commonCfg,
      key: 'next_step',
      header: 'Next Steps',
      onChange,
    }),
  ] as TableColumn<IDPSReportChangeVO>[];
};
